import { db } from '@/lib/firebase';
import { collection, addDoc, getDocs, getDoc, updateDoc, deleteDoc, query, where, doc, serverTimestamp } from 'firebase/firestore';
import { jobSchema, insertJobSchema, type Job, type InsertJob, JobStatus } from '@/lib/schema';

export const getJobs = async (filters: { recruiterId?: string; status?: JobStatus; search?: string; location?: string; sortBy?: string } = {}) => {
  let q = query(collection(db, 'jobs'));
  if (filters.recruiterId) q = query(q, where('recruiterId', '==', filters.recruiterId));
  if (filters.status) q = query(q, where('status', '==', filters.status));
  const snapshot = await getDocs(q);
  
  // Parse all jobs with proper schema validation
  let jobs = snapshot.docs.map(d => {
    const data = d.data();
    return jobSchema.parse({
      id: d.id, // Include the document ID
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      publishedAt: data.publishedAt?.toDate(),
      expiresAt: data.expiresAt?.toDate()
    });
  }) as Job[];
  
  // Client-side filtering
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    jobs = jobs.filter(job => 
      job.title.toLowerCase().includes(searchLower) || 
      job.description.toLowerCase().includes(searchLower) ||
      job.skills.some(skill => skill.toLowerCase().includes(searchLower))
    );
  }
  
  if (filters.location && filters.location !== 'all') {
    jobs = jobs.filter(job => job.location?.toLowerCase() === filters.location!.toLowerCase());
  }
  
  // Sorting
  if (filters.sortBy === 'recent') {
    jobs.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  } else if (filters.sortBy === 'priority') {
    const priorityOrder = { 'urgent': 4, 'high': 3, 'normal': 2, 'low': 1 };
    jobs.sort((a, b) => priorityOrder[b.priority] - priorityOrder[a.priority]);
  }
  
  return jobs;
};

export const getJob = async (id: string) => {
  const jobDoc = await getDoc(doc(db, 'jobs', id));
  if (!jobDoc.exists()) return null;
  const data = jobDoc.data();
  return jobSchema.parse({
    id: jobDoc.id,
    ...data,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
    publishedAt: data.publishedAt?.toDate(),
    expiresAt: data.expiresAt?.toDate()
  }) as Job;
};

export const createJob = async (data: InsertJob) => {
  const parsed = insertJobSchema.parse(data);
  const jobData = {
    ...parsed,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    // Set publishedAt if status is active
    ...(parsed.status === 'active' && { publishedAt: serverTimestamp() })
  };
  const ref = await addDoc(collection(db, 'jobs'), jobData);
  return getJob(ref.id);
};

export const updateJob = async (id: string, updates: Partial<Job>) => {
  const updateData = {
    ...updates,
    updatedAt: serverTimestamp(),
    // Set publishedAt if status is changing to active
    ...(updates.status === 'active' && { publishedAt: serverTimestamp() })
  };
  
  // Filter out undefined values to prevent Firestore errors
  const cleanUpdates = Object.fromEntries(
    Object.entries(updateData).filter(([_, value]) => value !== undefined)
  );
  
  await updateDoc(doc(db, 'jobs', id), cleanUpdates);
  return getJob(id);
};

export const deleteJob = async (id: string) => {
  await deleteDoc(doc(db, 'jobs', id));
  return true;
};
